import {
  VoxProductCatalogItem,
  VoxRoiCostConfigData,
  VoxRoiUsageResponse,
  VoxRoiProductUsageItem,
  VoxRoiAfterLine,
  VoxRoiBeforeProvider,
  VoxRoiResult,
  CostScheduleEntry,
  CostGapWarning,
  BeforeProviderEntry,
} from '../../models/voxRoi';

/** Normalize a date value (string or Date from the convertDates interceptor) to YYYY-MM-DD. */
const toDateString = (value: string | Date): string => {
  if (value instanceof Date) return value.toISOString().substring(0, 10);
  return value.substring(0, 10);
};

/** Find the cost entry covering a given date, or undefined if none. */
const findEffectiveCost = (schedule: CostScheduleEntry[] | undefined, date: string): number | undefined => {
  if (!schedule || schedule.length === 0) return undefined;
  for (const entry of schedule) {
    if (date >= entry.effectiveFrom && (entry.effectiveTo === null || date <= entry.effectiveTo)) {
      return entry.cost;
    }
  }
  return undefined;
};

/** G7: Validate schedule entries — reject overlapping ranges per product. */
export const validateSchedule = (schedule: CostScheduleEntry[]): string | null => {
  if (schedule.length <= 1) return null;
  const sorted = [...schedule].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    if (prev.effectiveTo === null || prev.effectiveTo >= curr.effectiveFrom) {
      return `Overlapping schedule entries: ${prev.effectiveFrom}–${prev.effectiveTo ?? 'open'} and ${curr.effectiveFrom}–${curr.effectiveTo ?? 'open'}`;
    }
  }
  return null;
};

export const computeRoi = ({
  config,
  catalog,
  usage,
}: {
  config: VoxRoiCostConfigData;
  catalog: VoxProductCatalogItem[];
  usage: VoxRoiUsageResponse;
}): VoxRoiResult => {
  const { totalUnitCount, unitCounts, productUsage } = usage;
  const { costs, beforeProviders: beforeProviderConfig } = config;

  const catalogByKey = new Map(catalog.map((item) => [item.productKey, item]));

  // --- BEFORE (flat per-provider cost, no date-effective repricing) ---
  const beforeKeys = Object.keys(beforeProviderConfig);
  const providerCount = beforeKeys.length;

  const beforeCostPerLoan = beforeKeys.reduce((sum, key) => {
    const entry: BeforeProviderEntry = beforeProviderConfig[key];
    return sum + entry.ordersPerLoan * entry.cost;
  }, 0);
  const beforeTotalSpend = beforeCostPerLoan * totalUnitCount;

  const beforeProviders: VoxRoiBeforeProvider[] = beforeKeys.map((key) => {
    const catalogItem = catalogByKey.get(key);
    const entry = beforeProviderConfig[key];
    return {
      productKey: key,
      vendor: catalogItem?.vendor ?? 'Unknown',
      productDisplayName: catalogItem?.productDisplayName ?? key,
      ordersPerLoan: entry.ordersPerLoan,
      costPerOrder: entry.cost,
    };
  });

  // --- AFTER (per-day, date-effective) ---
  const unmappedUsage: VoxRoiProductUsageItem[] = [];
  const costGapMap = new Map<string, Set<string>>();
  const afterLineMap = new Map<string, VoxRoiAfterLine>();
  let afterTotalSpend = 0;

  for (const usageItem of productUsage) {
    const { productKey, successCount, date } = usageItem;
    const catalogProduct = catalogByKey.get(productKey);

    if (!catalogProduct) {
      unmappedUsage.push(usageItem);
      continue;
    }

    const dateStr = toDateString(date);
    const unitCost = findEffectiveCost(costs[productKey], dateStr);

    if (unitCost === undefined) {
      const existing = costGapMap.get(productKey);
      if (existing) {
        existing.add(dateStr);
      } else {
        costGapMap.set(productKey, new Set([dateStr]));
      }
    }

    const cost = unitCost ?? 0;
    const lineTotal = cost * successCount;
    afterTotalSpend += lineTotal;

    const existing = afterLineMap.get(productKey);
    if (existing) {
      existing.successCount += successCount;
      existing.lineTotal += lineTotal;
      existing.unitCost = existing.lineTotal / existing.successCount;
    } else {
      afterLineMap.set(productKey, {
        productKey,
        vendor: catalogProduct.vendor,
        productDisplayName: catalogProduct.productDisplayName,
        successCount,
        unitCost: cost,
        lineTotal,
      });
    }
  }

  const afterLines = Array.from(afterLineMap.values());
  const afterAvgCostPerLoan = totalUnitCount > 0 ? afterTotalSpend / totalUnitCount : 0;

  // G8: cost-schedule gap warnings
  const productsMissingCost: CostGapWarning[] = Array.from(costGapMap.entries()).map(
    ([productKey, dates]) => ({ productKey, dates: Array.from(dates).sort() }),
  );

  // --- SAVINGS ---
  const savingsTotal = beforeTotalSpend - afterTotalSpend;
  const savingsPct = beforeTotalSpend > 0
    ? savingsTotal / beforeTotalSpend
    : null;
  const savingsPerLoan = beforeCostPerLoan - afterAvgCostPerLoan;

  return {
    totalUnitCount,
    before: { costPerLoan: beforeCostPerLoan, totalSpend: beforeTotalSpend, providerCount, providers: beforeProviders },
    after: { totalSpend: afterTotalSpend, avgCostPerLoan: afterAvgCostPerLoan, lines: afterLines },
    savings: { total: savingsTotal, pct: savingsPct, perLoan: savingsPerLoan },
    warnings: { unmappedUsage, productsMissingCost },
  };
};
